import { makeRule } from "../stylemap.js";
import { findOverlaps, orderedIndexes } from "../order.js";
import { ruleName, ruleKind, kindLabel } from "../labels.js";
import { classifyMessages } from "../warnings.js";
import { convert } from "../convert.js";
import { buildSpecimen } from "../specimen.js";
import { importProfile, profileToJson, profileFilename } from "../storage.js";
import { stylesheetFilename, DOCX_MIME } from "../export.js";
import { WRAPPER_RE } from "../profiles/schema.js";
import {
  state, store, ensureWorking, updateWorking, saveWorking, isDirty, isSavedCustom, takenIds, startUnsaved,
} from "../state.js";
import { el, srOnly, actionButton, field, announce, debounce, downloadBlob, setBusy } from "../ui/dom.js";
import { chooseUnsaved, showMessages } from "../ui/dialogs.js";

const SHOW = [["all", "All"], ["unmapped", "Not mapped"], ["overlapping", "Overlapping"], ["plain", "Plain"]];
const TYPES = [["any", "Any type"], ["paragraph", "Paragraph"], ["character", "Character"], ["table", "Table"], ["list", "List"], ["formatting", "Formatting"]];

export function mount(root, route, nav) {
  if (!ensureWorking(route.id)) {
    queueMicrotask(() => nav.go("#/profiles"));
    return {};
  }
  if (!state.doc) state.previewSource = "sample";
  const here = () => `#/profiles/${encodeURIComponent(state.working.id)}`;
  let unmapped = [];
  let analysing = true;

  // ---- Actions bar ----
  const title = el("h1", {});
  const status = el("p", { class: "bar-status" });
  const saveButton = actionButton("Save", "profile in this browser", { class: "accent-btn", onclick: save });
  const importInput = el("input", { type: "file", accept: ".json,application/json", class: "sr-only", tabindex: "-1", "aria-hidden": "true" });
  const bar = el("div", { class: "actions-bar" },
    el("div", {}, title, status),
    el("div", { class: "row" },
      saveButton,
      actionButton("Download", "profile file (.json)", { onclick: () => downloadBlob(profileToJson(state.working), profileFilename(state.working), "application/json") }),
      actionButton("Import", "a profile file (.json)", { onclick: () => importInput.click() }),
      importInput,
      actionButton("Sample .docx", "download a Word document that uses every style in this profile", { onclick: sample }),
      actionButton("Edit as code", "edit the whole profile as JSON", { onclick: () => nav.go(`${here()}/code`) }),
      actionButton("Done", state.doc ? "return to my document" : "return to Convert", { onclick: () => nav.go(state.doc ? "#/result" : "#/") })));

  function refreshBar() {
    const w = state.working;
    title.textContent = w.name;
    let text = w.builtIn ? "Built-in profile · your first change creates a copy"
      : !isSavedCustom() ? "Not saved yet" : isDirty() ? "Unsaved changes" : "Saved in this browser";
    if (!store.available) text += " · saving isn't available in this browser, so download the profile to keep it";
    status.textContent = text;
    saveButton.disabled = w.builtIn || !isDirty() || !store.available;
  }

  function change(mutate) {
    const next = structuredClone(state.working);
    mutate(next);
    if (updateWorking(next)) nav.replace(here());
    refreshBar();
  }

  function save() {
    try {
      saveWorking();
      announce("Profile saved in this browser.");
    } catch (error) {
      showMessages("Couldn't save", [error.message]);
    }
    refreshBar();
  }

  async function sample(event) {
    const button = event.currentTarget;
    setBusy(button, true);
    try {
      downloadBlob(await buildSpecimen(state.working), stylesheetFilename(state.working), DOCX_MIME);
      announce("Sample Word document downloaded.");
    } catch (error) {
      showMessages("Couldn't build the sample document", [error.message]);
    } finally {
      setBusy(button, false);
    }
  }

  importInput.addEventListener("change", async () => {
    const file = importInput.files[0];
    importInput.value = "";
    if (!file) return;
    const { profile, errors } = importProfile(await file.text(), takenIds());
    if (!profile) {
      showMessages("Couldn't import that profile", errors);
      return;
    }
    if (isDirty()) {
      const choice = await chooseUnsaved({ message: `“${state.working.name}” has changes you haven't saved.`, saveLabel: "Save profile" });
      if (choice === "cancel") return;
      if (choice === "save") {
        try {
          saveWorking();
        } catch (error) {
          showMessages("Couldn't save", [error.message]);
          return;
        }
      }
    }
    startUnsaved(profile);
    nav.go(here());
    if (errors.length) showMessages("Imported, with some problems", errors);
  });

  // ---- Settings ----
  function settings() {
    const w = state.working;
    const input = (id, label, key, hint, { kind = "text", validate } = {}) => {
      const value = kind === "list" ? w[key].join("\n") : w[key];
      const control = kind === "text"
        ? el("input", { type: "text", value })
        : el("textarea", { rows: kind === "list" ? 2 : 4, value, class: key === "extraCss" ? "mono" : "" });
      const wrapper = field({ id, label, hint, control });
      const error = el("p", { class: "field-error", id: `${id}-error`, hidden: true });
      wrapper.append(error);
      control.setAttribute("aria-describedby", `${id}-hint ${id}-error`);
      control.addEventListener("input", () => {
        const raw = control.value;
        const problem = validate?.(raw.trim()) ?? null;
        error.textContent = problem ?? "";
        error.hidden = !problem;
        if (problem) control.setAttribute("aria-invalid", "true");
        else control.removeAttribute("aria-invalid");
        if (problem) return;
        change((next) => {
          next[key] = kind === "list" ? raw.split(/\r?\n/).map((s) => s.trim()).filter(Boolean)
            : key === "extraCss" || key === "description" ? raw : raw.trim();
        });
      });
      return wrapper;
    };
    const wrapperCheck = (v) => (v === "" || WRAPPER_RE.test(v) ? null : "Use an element and classes, e.g. div.content or section > div.container.");
    return el("details", { class: "settings glass" },
      el("summary", {}, "Profile settings"),
      el("div", { class: "settings-grid" },
        input("set-name", "Name", "name", "Shown in profile lists.", { validate: (v) => (v ? null : "Enter a name.") }),
        input("set-description", "Description", "description", "One line explaining what this profile is for."),
        input("set-css", "Preview CSS", "css", "Stylesheet URLs used in the preview, one per line.", { kind: "list" }),
        input("set-export-css", "Export CSS", "exportCss", "Stylesheet URLs linked from downloaded HTML files, one per line.", { kind: "list" }),
        input("set-extra-css", "Extra CSS", "extraCss", "CSS added to the preview and to downloads.", { kind: "area" }),
        input("set-wrapper", "Wrapper for copied HTML", "wrapper", "Element the HTML is wrapped in when copied, e.g. div.content. Leave empty for none.", { validate: wrapperCheck }),
        input("set-export-wrapper", "Wrapper for full pages", "exportWrapper", "Used in downloaded HTML files, e.g. div.container > div.content.", { validate: wrapperCheck })));
  }

  // ---- Preview source ----
  const radio = (name, value, label, checked, onchange) => el("label", {},
    el("input", { type: "radio", name, value, checked, onchange }), label);
  const sourceControl = state.doc
    ? el("fieldset", { class: "radios-inline" },
      el("legend", {}, "Check rules against"),
      radio("source", "document", `My document (${state.doc.name})`, state.previewSource === "document", () => { state.previewSource = "document"; analyse(); }),
      radio("source", "sample", "The sample document", state.previewSource === "sample", () => { state.previewSource = "sample"; analyse(); }))
    : null;

  // ---- Filters ----
  const search = el("input", { type: "search", value: state.filters.search, placeholder: "Style name, type or HTML" });
  search.addEventListener("input", debounce(() => { state.filters.search = search.value; renderCards(); }, 150));
  const showGroup = el("fieldset", { class: "segmented" }, el("legend", {}, "Show"),
    SHOW.map(([value, label]) => el("label", { class: "seg" },
      el("input", {
        type: "radio", name: "show", value, checked: state.filters.show === value,
        onchange: () => { state.filters.show = value; renderCards(); },
      }),
      el("span", { class: "seg-face" }, label, el("span", { dataset: { count: value } })))));
  const typeSelect = el("select", {}, TYPES.map(([value, label]) => el("option", { value }, label)));
  typeSelect.value = state.filters.type;
  typeSelect.addEventListener("change", () => { state.filters.type = typeSelect.value; renderCards(); });
  const count = el("p", { class: "hint", role: "status" });
  const grid = el("ul", { class: "card-grid", "aria-label": "Rules" });

  // ---- Cards ----
  function ruleCard(index, rule, overlap) {
    const outcome = overlap ? (overlap.wins ? "wins" : "loses") : "";
    const against = overlap ? overlap.with.map((i) => `“${ruleName(state.working.rules[i])}”`).join(", ") : "";
    return el("button", {
      type: "button",
      class: `rule-card${rule.group === "quiet" ? " plain" : ""}${overlap ? " overlapping" : ""}`,
      onclick: () => nav.go(`${here()}/rules/${index}`),
    },
    el("span", { class: "card-name" }, ruleName(rule)),
    el("span", { class: "card-kind" }, srOnly(", "), kindLabel(rule)),
    overlap ? el("span", { class: "card-note" }, srOnly(", "), `Overlaps ${against} · ${outcome}`) : null,
    srOnly(", edit rule"));
  }

  function unmappedCard(item) {
    const paragraph = item.type === "paragraph";
    return el("button", {
      type: "button",
      class: "rule-card unmapped",
      onclick: () => {
        state.ruleDraft = {
          index: "new",
          rule: makeRule({ match: paragraph ? "p" : "r", style: item.name, html: paragraph ? "p:fresh" : "span" }),
        };
        nav.go(`${here()}/rules/new`);
      },
    },
    el("span", { class: "card-name" }, item.name),
    el("span", { class: "card-kind" }, srOnly(", "), `${paragraph ? "Paragraph" : "Character"} style · not mapped`),
    el("span", { class: "card-note" }, srOnly(", "), `Used ${item.count} time${item.count === 1 ? "" : "s"}`),
    srOnly(", add a rule"));
  }

  function renderCards() {
    const w = state.working;
    const overlaps = findOverlaps(w.rules);
    const items = [
      ...unmapped.map((u) => ({ kind: u.type === "paragraph" ? "paragraph" : "character", unmapped: u })),
      ...orderedIndexes(w.rules).map((index) => ({ kind: ruleKind(w.rules[index]), index, rule: w.rules[index] })),
    ];
    const counts = {
      all: items.length,
      unmapped: unmapped.length,
      overlapping: overlaps.size,
      plain: w.rules.filter((r) => r.group === "quiet").length,
    };
    for (const [value] of SHOW) showGroup.querySelector(`[data-count="${value}"]`).textContent = ` (${counts[value]})`;
    const query = state.filters.search.trim().toLowerCase();
    const { show, type } = state.filters;
    const visible = items.filter((item) => {
      if (type !== "any" && item.kind !== type) return false;
      if (show === "unmapped" && !item.unmapped) return false;
      if (show === "overlapping" && !(item.rule && overlaps.has(item.index))) return false;
      if (show === "plain" && !(item.rule && item.rule.group === "quiet")) return false;
      if (!query) return true;
      const text = item.unmapped
        ? `${item.unmapped.name} ${item.kind} not mapped`
        : `${ruleName(item.rule)} ${kindLabel(item.rule)} ${item.rule.html ?? ""}`;
      return text.toLowerCase().includes(query);
    });
    grid.replaceChildren(...visible.map((item) => el("li", {},
      item.unmapped ? unmappedCard(item.unmapped) : ruleCard(item.index, item.rule, overlaps.get(item.index)))));
    setBusy(grid, analysing);
    count.textContent = `Showing ${visible.length} of ${items.length} rules${analysing ? " · checking for unmapped styles…" : ""}`;
  }

  let analyseToken = 0;
  async function analyse() {
    const mine = ++analyseToken;
    analysing = true;
    renderCards();
    try {
      const data = state.previewSource === "document" && state.doc ? state.doc.data : await buildSpecimen(state.working);
      const { messages } = await convert(data, state.working);
      if (mine !== analyseToken) return;
      const { customStyles, builtinStyles } = classifyMessages(messages);
      unmapped = [...customStyles, ...builtinStyles];
    } catch (error) {
      console.error(error);
      unmapped = [];
    }
    analysing = false;
    renderCards();
  }

  root.append(el("div", { class: "editor-page" },
    bar,
    settings(),
    sourceControl,
    el("div", { class: "filter-bar" },
      field({ id: "rule-search", label: "Search rules", hint: "Matches style name, type or HTML output.", control: search }),
      showGroup,
      field({ id: "rule-type", label: "Type", control: typeSelect })),
    count,
    grid,
    el("div", { class: "row" }, actionButton("Add a rule", "to this profile", { class: "primary-btn", onclick: () => { state.ruleDraft = null; nav.go(`${here()}/rules/new`); } }))));
  refreshBar();
  analyse();
  return { title: `Edit ${state.working.name}` };
}
